import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

const SiteConfigContext = createContext(null);

const DEFAULT_CONFIG = {
  identity: {
    site_name: 'Dalaa Beauty Center & Academy',
    logo: null,
    favicon: null,
    colors: {
      primary: '#c4477a',
      primary_dark: '#a83668',
      accent: '#b54e7d',
      text_dark: '#5a1d3b',
      bg_light: '#fdf5f7',
    },
  },
  hero: { welcome: {}, title: {}, description: {}, cta_text: {}, logo_image: null, bg_image: null },
  contact: {},
  footer: { tagline: {}, rights: {} },
  nav: [],
  services: [],
  gallery: [],
  pricing: [],
  products: [],
};

export const SiteConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/config');
      setConfig({
        identity: data.identity || DEFAULT_CONFIG.identity,
        hero: data.hero || DEFAULT_CONFIG.hero,
        contact: data.contact || DEFAULT_CONFIG.contact,
        footer: data.footer || DEFAULT_CONFIG.footer,
        nav: data.nav || [],
        services: data.services || [],
        gallery: data.gallery || [],
        pricing: data.pricing || [],
        products: data.products || [],
      });
    } catch (e) {
      console.error('Failed to load site config', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Apply primary color & favicon to document
  useEffect(() => {
    const c = config.identity?.colors;
    if (c) {
      const root = document.documentElement;
      Object.entries(c).forEach(([k, v]) => {
        root.style.setProperty(`--brand-${k.replace(/_/g, '-')}`, v);
      });
    }
    // Favicon
    const fav = config.identity?.favicon;
    if (fav) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = fav;
    }
    // Title
    if (config.identity?.site_name) {
      document.title = config.identity.site_name;
    }
  }, [config.identity]);

  return (
    <SiteConfigContext.Provider value={{ config, loading, refresh }}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export const useSiteConfig = () => {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) throw new Error('useSiteConfig must be inside SiteConfigProvider');
  return ctx;
};

// Helper: pick i18n field with fallback
export const pickI18n = (obj, lang, fallback = 'de') => {
  if (!obj) return '';
  return obj[lang] || obj[fallback] || obj.en || obj.de || '';
};
